import { round, uniqBy } from "lodash";
import { FullProduct } from "ts/api/product";
import { OrganiserTransfer, SupplierTransfer } from "ts/api/transfer";
import {
  IOrganiser,
  IStripeConnectAccountPublic,
} from "@evennt-tech/api-v2-core";
import { calculateFullProductDiscount } from "tools/product/productCalculations/calculateFullProductPrice";
import { v4 as uuid } from "uuid";
import { OrderProduct } from "ts/api/order";
import { calculateSupplierPlatformFeeAmount } from "view/apps/Exhibitor/pages/Cart/helpers";
import { BankTransferBillingDetails } from "store/exhibitors/cartStepper/slice";

type OrganiserTransferParams = {
  organiser: IOrganiser;
  suppliersTransfers: SupplierTransfer[];
  stripeAccountId: string;
  metadata: OrganiserTransferMetadataContext;
};
export type OrganiserTransferMetadataContext = {
  orderId?: string;
  showName: string;
  showStartDate: string;
};
type TransferMetadataContext = OrganiserTransferMetadataContext & {
  invoiceNumber?: string;
  billingDetails?: BankTransferBillingDetails;
};

export type TransferTax = {
  name: string;
  percentage: number;
};

const calculateTaxBreakdown = (
  subtotalAmount: number,
  taxes: number | TransferTax[]
) => {
  if (!Array.isArray(taxes)) {
    return {
      taxAmount: round((subtotalAmount * taxes) / 100, 2),
      taxes: {} as Record<string, number>,
    };
  }

  const taxesByName = taxes.reduce<Record<string, number>>(
    (acc, { name, percentage }) => {
      const amount = round((subtotalAmount * percentage) / 100, 2);
      acc[name] = round((acc[name] || 0) + amount, 2);
      return acc;
    },
    {}
  );
  const taxAmount = round(
    Object.values(taxesByName).reduce((acc, amount) => acc + amount, 0),
    2
  );

  return {
    taxAmount,
    taxes: taxesByName,
  };
};

const calculateDiscountAmount = (
  products: (FullProduct | OrderProduct)[]
): number => {
  const bigAmount = products.reduce(
    (acc, curr) => acc + calculateFullProductDiscount(curr, false),
    0
  );

  return bigAmount;
};

export const calculateSupplierTransferAmount = (
  products: (FullProduct | OrderProduct)[],
  ownerId: string,
  taxes: number | TransferTax[],
  feesAmount: number
) => {
  const currentSupplierProducts = products.filter(
    (product) => product.owner.id === ownerId
  );

  const productsFullAmount = currentSupplierProducts
    .map((product) => {
      const productPrice =
        (product.price - (product.discountAmount ?? 0)) *
        round(
          (product as FullProduct).cartQuantity || product.quantity || 1,
          2
        );
      const extrasPrice = product?.extras?.length
        ? product.extras.reduce(
            (acc, cur) =>
              acc +
              (cur.price - (cur.discountAmount ?? 0)) * round(cur.quantity, 2),
            0
          )
        : 0;

      return extrasPrice + productPrice;
    })
    .reduce((acc, val) => acc + val, 0);
  const discountAmount = calculateDiscountAmount(currentSupplierProducts);
  const productsAmount = Math.max(0, productsFullAmount);
  const subtotalAmount = round(feesAmount + productsAmount, 2);
  const { taxAmount, taxes: taxesByName } = calculateTaxBreakdown(
    subtotalAmount,
    taxes
  );
  const totalAmount = round(subtotalAmount + taxAmount, 2);

  return {
    productsAmount: round(productsAmount, 2),
    subtotalAmount,
    taxes: taxesByName,
    taxAmount,
    totalAmount,
    discountAmount: round(discountAmount, 2),
  };
};

export const getSuppliersTransfers = (
  products: FullProduct[],
  taxes: TransferTax[],
  transferredPaymentDescription: string,
  supplierAccounts: Record<string, IStripeConnectAccountPublic>,
  metadataContext: TransferMetadataContext = {} as TransferMetadataContext
): (SupplierTransfer & { transferredPaymentDescription: string })[] => {
  const {
    orderId,
    invoiceNumber,
    billingDetails,
    showStartDate,
    showName,
  } = metadataContext;
  const uniqueSuppliers = uniqBy(products, "owner.id");

  const transfers = uniqueSuppliers.map(({ owner, ownerToShow }) => {
    const currentSupplierProducts = products.filter(
      (product) => product.owner.id === owner.id
    );

    const fees = uniqBy(
      currentSupplierProducts.map((product) => product?.fees).flat(1),
      "id"
    );
    const feesAmount = fees.reduce((acc, cur) => acc + (cur?.amount || 0), 0);
    const platformFeeAmount = calculateSupplierPlatformFeeAmount(
      currentSupplierProducts
    );
    const paymentDescription = `${transferredPaymentDescription}, ${owner.name}`;

    const supplierAccount = supplierAccounts[owner.id];

    if (!supplierAccount) {
      throw new Error(`Supplier ${owner.name} does not have a Stripe account`);
    }

    const { accountId } = supplierAccount;

    if (!accountId) {
      throw new Error(`Supplier ${owner.name} does not have a Stripe account`);
    }

    const {
      productsAmount,
      subtotalAmount,
      taxes: taxesByName,
      taxAmount,
      totalAmount,
      discountAmount,
    } = calculateSupplierTransferAmount(products, owner.id, taxes, feesAmount);
    const organiserFee = ownerToShow?.organiserFee ?? 0;
    const organiserFeeAmount = round((totalAmount * organiserFee) / 100, 2);
    const invoiceNumberFromProducts = currentSupplierProducts
      .map((product) => (product as { invoiceId?: string }).invoiceId)
      .find((currentInvoiceNumber) => !!currentInvoiceNumber);

    return {
      metadata: {
        id: owner.id,
        role: "supplier",
        orderId,
        invoiceNumber: invoiceNumber || invoiceNumberFromProducts,
        organiserFee,
        organiserFeeAmount,
        ffairFeeAmount: platformFeeAmount,
        taxes: JSON.stringify(taxesByName),
        feesAmount,
        productsAmount,
        subtotalAmount,
        taxAmount,
        totalAmount,
        discountAmount,
        showName,
        showStartDate,
        ...billingDetails,
      },
      destination: accountId,
      amount: totalAmount,
      transferredPaymentDescription: paymentDescription,
      id: uuid(),
    };
  });

  return transfers;
};

const calculateOrganiserTransferAmount = (
  suppliersTransfers: Omit<SupplierTransfer, "stripeId">[]
) => {
  const transferAmount = suppliersTransfers
    .map((transfer) => transfer.amount * (transfer.metadata.organiserFee / 100))
    .reduce((acc, val) => acc + val, 0);

  return round(transferAmount, 2);
};

export const getOrganiserTransfer = ({
  organiser,
  suppliersTransfers,
  stripeAccountId,
  metadata,
}: OrganiserTransferParams): Omit<OrganiserTransfer, "stripeId"> => {
  const organiserTransfer = {
    metadata: {
      id: organiser.id,
      role: "organiser",
      ...metadata,
    },
    destination: stripeAccountId as string,
    amount: calculateOrganiserTransferAmount(suppliersTransfers),
    id: uuid(),
  };

  return organiserTransfer;
};
